import { useMemo } from 'react';
import { WeeklyAttendanceList } from '../WeeklyAttendanceList';
import { CheckInCenter } from '../CheckInCenter';
import { ListSkeleton } from '../../atoms/Skeleton';
import type { CheckInStatus } from '../../../types';
import type { DailyAttendanceCardProps, WeeklyCompactDay, WeeklySummary } from '../../../types';
import * as s from './AttendanceDashboard.css';

export interface AttendanceDashboardProps {
  weeklyDays: DailyAttendanceCardProps[];
  dailyTargetMs?: number;
  checkInStatus?: CheckInStatus;
  elapsedSeconds?: number;
  onCheckIn?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onCheckOut?: () => void;
  checkInLoading?: boolean;

  /** 주간 근무 데이터 로딩 중 여부 */
  weeklyLoading?: boolean;
  /** 스케줄 기반 주간 요약 (백엔드 계산) */
  weeklySummary?: WeeklySummary;
}

function toCompactDays(days: DailyAttendanceCardProps[]): WeeklyCompactDay[] {
  return days.map((d) => ({
    date: d.date,
    clockInAt: d.clockInAt,
    clockOutAt: d.clockOutAt,
    segments: d.segments
      .filter((seg) => seg.type === 'WORK')
      .map((seg) => ({ startAt: seg.startAt, endAt: seg.endAt })),
    totalWorkDurationMs: d.totalWorkDurationMs,
    isToday: d.isToday,
    isWorking: d.status === 'CLOCKED_IN' || d.status === 'ON_BREAK',
  }));
}

export function AttendanceDashboard({
  weeklyDays,
  dailyTargetMs,
  checkInStatus,
  elapsedSeconds = 0,
  onCheckIn,
  onPause,
  onResume,
  onCheckOut,
  checkInLoading,
  weeklyLoading = false,
  weeklySummary,
}: AttendanceDashboardProps) {
  const todayData = useMemo(() => weeklyDays.find((day) => day.isToday), [weeklyDays]);
  const compactDays = useMemo(() => toCompactDays(weeklyDays), [weeklyDays]);
  const checkInTodayData = useMemo(
    () =>
      todayData
        ? {
            status: todayData.status,
            clockInAt: todayData.clockInAt,
            clockOutAt: todayData.clockOutAt,
            totalWorkDurationMs: todayData.totalWorkDurationMs,
            segments: todayData.segments,
            totalBreakDurationMs: todayData.totalBreakDurationMs,
            note: todayData.note,
          }
        : undefined,
    [todayData]
  );

  const showCheckInCenter =
    checkInStatus !== undefined && onCheckIn && onPause && onResume && onCheckOut;

  return (
    <div className={s.container}>
      {showCheckInCenter && (
        <CheckInCenter
          checkInStatus={checkInStatus}
          elapsedSeconds={elapsedSeconds}
          onCheckIn={onCheckIn}
          onPause={onPause}
          onResume={onResume}
          onCheckOut={onCheckOut}
          isLoading={checkInLoading}
          todayData={checkInTodayData}
          targetWorkDurationMs={dailyTargetMs}
        />
      )}

      {weeklyLoading ? (
        <ListSkeleton count={5} />
      ) : (
        <WeeklyAttendanceList
          days={compactDays}
          dailyTargetMs={dailyTargetMs}
          summary={weeklySummary}
        />
      )}
    </div>
  );
}
